import { confirmSignIn, resendSignUpCode, signIn } from "aws-amplify/auth";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AuthLayout } from "../components/AuthLayout";
import { ErrorNotice, Field, useAction } from "../components/ui";
import { useSession } from "../lib/session";

export function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useSession();
  const { pending, error, setError, run } = useAction();
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [needsCode, setNeedsCode] = useState(false);
  const [code, setCode] = useState("");

  const finish = async () => {
    await refresh();
    navigate(params.get("next") ?? "/", { replace: true });
  };

  const submitPassword = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const { nextStep } = await signIn({ username: email.trim().toLowerCase(), password });
      switch (nextStep.signInStep) {
        case "DONE":
          return finish();
        case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
          setNeedsCode(true);
          return;
        case "CONFIRM_SIGN_UP":
          await resendSignUpCode({ username: email.trim().toLowerCase() });
          navigate(`/signup?confirm=${encodeURIComponent(email.trim().toLowerCase())}`);
          return;
        default:
          setError("This login needs a step the app doesn't support yet. Contact support.");
      }
    });
  };

  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const { nextStep } = await confirmSignIn({ challengeResponse: code.trim() });
      if (nextStep.signInStep === "DONE") await finish();
    });
  };

  return (
    <AuthLayout
      masthead={
        <h1 className="display">
          Back to the <em>desk</em>.
        </h1>
      }
    >
      {needsCode ? (
        <form className="form" onSubmit={submitCode}>
          <div className="page-head">
            <p className="label">Step 2 of 2</p>
            <h2 className="title">
              Your <em>code</em>
            </h2>
            <p className="lede">Enter the 6-digit code from your authenticator app.</p>
          </div>
          <Field
            label="Authenticator code"
            className="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <ErrorNotice error={error} />
          <button className="btn block" disabled={pending}>
            {pending ? "Checking…" : "Log in"}
          </button>
        </form>
      ) : (
        <form className="form" onSubmit={submitPassword}>
          <div className="page-head">
            <h2 className="title">
              Log <em>in</em>
            </h2>
          </div>
          <Field label="Email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <Field
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {params.get("reset") === "done" && !error && <p className="notice ok">Password changed. Log in with the new one.</p>}
          <ErrorNotice error={error} />
          <button className="btn block" disabled={pending}>
            {pending ? "Logging in…" : "Log in"}
          </button>
          <p className="sheet-foot">
            <Link to={`/reset-password${email.trim().toLowerCase() ? `?email=${encodeURIComponent(email.trim().toLowerCase())}` : ""}`}>Forgot your password?</Link>
          </p>
          <p className="sheet-foot">
            New here? <Link to={`/signup${params.toString() ? `?${params}` : ""}`}>Create a login</Link>
          </p>
        </form>
      )}
    </AuthLayout>
  );
}
